<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row class="align-items-center">
          <b-col cols="12" md="8">
            <b-row>
              <b-col cols="4" class="text-center">
                <h6 class="text-muted mb-25">Всего</h6>
                <h3 class="mb-0">{{ summary.total }}</h3>
              </b-col>
              <b-col cols="4" class="text-center">
                <h6 class="text-muted mb-25">Завершено</h6>
                <h3 class="mb-0">{{ summary.completed }}</h3>
              </b-col>
              <b-col cols="4" class="text-center">
                <h6 class="text-muted mb-25">Проблемных</h6>
                <h1 class="mb-0 text-danger">{{ summary.problems }}</h1>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" md="4" class="text-md-right mt-2 mt-md-0">
            <small v-if="cachedAt" class="text-muted d-block mb-50">
              Данные на {{ formatDateTime(cachedAt) }}
            </small>
            <b-button
              v-if="$can('manage', 'author-tasks')"
              variant="outline-primary"
              size="sm"
              :disabled="isRefreshing"
              @click="refresh"
            >
              <b-spinner v-if="isRefreshing" small class="mr-50" />
              <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
              Обновить из ПФ
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <label>Статус</label>
            <b-form-select v-model="statusFilter" :options="statusOptions" />
          </b-col>
          <b-col cols="12" md="4" class="mb-1 d-flex align-items-end">
            <b-form-checkbox v-model="onlyProblems">
              Только проблемные
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            :rows="filteredTasks"
            :columns="columns"
            :sort-options="{ enabled: false }"
            :pagination-options="{ enabled: false }"
            :row-style-class="rowStyleClass"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Нет задач по выбранным фильтрам</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'task_id'">
                <b-link :href="props.row.task_url" target="_blank" rel="noopener noreferrer">
                  #{{ props.row.task_id }}
                </b-link>
              </span>
              <span v-else-if="props.column.field === 'name'">{{ props.row.name }}</span>
              <span v-else-if="props.column.field === 'author_name'">{{ props.row.author_name || '—' }}</span>
              <span v-else-if="props.column.field === 'status_name'">
                <b-badge :variant="statusVariant(props.row.status_id)">{{ props.row.status_name }}</b-badge>
              </span>
              <span v-else-if="props.column.field === 'pack_name'">
                <b-badge v-if="props.row.has_pack_problem" variant="danger">Нет пакета!</b-badge>
                <span v-else>{{ props.row.pack_name || '—' }}</span>
              </span>
              <span v-else-if="props.column.field === 'amount'">{{ formatRub(props.row.amount) }}</span>
              <span v-else-if="props.column.field === 'start_date'">{{ formatDate(props.row.start_date) }}</span>
              <span v-else-if="props.column.field === 'end_date'">{{ formatDate(props.row.end_date) }}</span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BSpinner, BOverlay, BLink, BBadge, BFormSelect, BFormCheckbox,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import {
  ref, computed, onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTime, formatDate } from '@core/utils/filter'
import { formatRub } from '@/views/finance/utils'
import authorTasksStoreModule from '@/views/author-tasks/authorTasksStoreModule'

const STORE_MODULE_NAME = 'app-author-tasks'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BOverlay,
    BLink,
    BBadge,
    BFormSelect,
    BFormCheckbox,
    VueGoodTable,
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, authorTasksStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const isLoading = ref(false)
    const isRefreshing = ref(false)
    const tasks = ref([])
    const summary = ref({ total: 0, completed: 0, problems: 0 })
    const cachedAt = ref(null)

    const statusFilter = ref('')
    const onlyProblems = ref(false)

    const columns = [
      { label: '№', field: 'task_id' },
      { label: 'Название', field: 'name' },
      { label: 'Автор', field: 'author_name' },
      { label: 'Статус', field: 'status_name' },
      { label: 'Пакет', field: 'pack_name' },
      { label: 'Сумма', field: 'amount', type: 'number' },
      { label: 'Начало', field: 'start_date' },
      { label: 'Конец', field: 'end_date' },
    ]

    const statusOptions = computed(() => {
      const uniqueStatuses = [...new Set(tasks.value.map(task => task.status_name))]
      return [{ value: '', text: 'Все' }, ...uniqueStatuses.map(status => ({ value: status, text: status }))]
    })

    const filteredTasks = computed(() => tasks.value.filter(task => {
      if (statusFilter.value && task.status_name !== statusFilter.value) return false
      if (onlyProblems.value && !task.has_pack_problem) return false
      return true
    }))

    // Точное написание status_name из живого Planfix не гарантировано, поэтому цвет бэджа
    // матчим по status_id — константы из Client::STATUS_AUTHOR_TASK_DONE (6) и
    // Client::STATUS_AUTHOR_TASK_PAID (111) на бэкенде
    const STATUS_ID_PAID = 111
    const STATUS_ID_DONE = 6

    const statusVariant = statusId => {
      if (statusId === STATUS_ID_PAID) return 'success'
      if (statusId === STATUS_ID_DONE) return 'info'
      return 'light-secondary'
    }

    const rowStyleClass = row => (row.has_pack_problem ? 'author-tasks-row-problem' : '')

    const applySnapshot = snapshot => {
      tasks.value = snapshot.data || []
      summary.value = snapshot.summary || { total: 0, completed: 0, problems: 0 }
      cachedAt.value = snapshot.cached_at || null
    }

    const fetchTasks = () => {
      isLoading.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchAuthorTasks`)
        .then(response => applySnapshot(response.data))
        .catch(err => showError(err.response?.data?.message || 'Ошибка загрузки задач авторов'))
        .finally(() => {
          isLoading.value = false
        })
    }

    // POST /refresh возвращает свежий снапшот синхронно — просто заменяем данные ответом,
    // поллинг cache-status (как в Финансах) здесь не нужен
    const refresh = () => {
      if (isRefreshing.value) return
      isRefreshing.value = true
      store.dispatch(`${STORE_MODULE_NAME}/refreshAuthorTasks`)
        .then(response => applySnapshot(response.data))
        .catch(err => showError(err.response?.data?.message || 'Не удалось обновить задачи из Планфикса'))
        .finally(() => {
          isRefreshing.value = false
        })
    }

    fetchTasks()

    return {
      isLoading,
      isRefreshing,
      tasks,
      summary,
      cachedAt,
      statusFilter,
      onlyProblems,
      statusOptions,
      columns,
      filteredTasks,
      statusVariant,
      rowStyleClass,
      refresh,
      formatDateTime,
      formatDate,
      formatRub,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.author-tasks-row-problem > td {
  background-color: rgba(234, 84, 85, 0.12);
}
</style>
